import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Download,
  History as HistoryIcon,
  Preview,
  Refresh,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { formatHistoryUserName } from './reportHistoryUtils';
import ReportPreviewDialog from './ReportPreviewDialog';
import ReportVisualizeDialog from './ReportVisualizeDialog';
import {
  downloadAgainstPoInOutReportHistoryCsv,
  downloadAgainstPoInOutReportHistoryPdf,
  downloadAgainstPoInOutReportCsv,
  downloadAgainstPoInOutReportPdf,
  fetchAgainstPoInOutReportPreviewData,
  fetchAgainstPoInOutReportHistory,
  resolveAxiosErrorMessage
} from '../../utils/reportCenterApi';

const DIRECTION_OPTIONS = [
  { value: 'ALL', label: 'PO IN + OUT' },
  { value: 'PO_OUT', label: 'PO OUT only (Purchase Orders)' },
  { value: 'PO_IN', label: 'PO IN only (Against PO Receiving)' }
];

const PO_STATUS_OPTIONS = [
  { value: '', label: 'All PO statuses' },
  { value: 'DRAFT', label: 'DRAFT' },
  { value: 'PENDING_STORE_APPROVAL', label: 'PENDING_STORE_APPROVAL' },
  { value: 'PENDING_AM_APPROVAL', label: 'PENDING_AM_APPROVAL' },
  { value: 'ISSUED', label: 'ISSUED' },
  { value: 'PARTIALLY_RECEIVED', label: 'PARTIALLY_RECEIVED' },
  { value: 'RECEIVED', label: 'RECEIVED' },
  { value: 'CLOSED', label: 'CLOSED' },
  { value: 'CANCELLED', label: 'CANCELLED' }
];

const WORKFLOW_OPTIONS = [
  { value: '', label: 'All receiving statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_INSPECTOR_APPROVAL', label: 'Pending inspection' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' }
];

function todayDateValue() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function addDays(dateValue, days) {
  const d = new Date(`${dateValue}T00:00:00`);
  d.setDate(d.getDate() + days);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function formatDisplayDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDisplayDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function directionLabel(value) {
  const found = DIRECTION_OPTIONS.find((opt) => opt.value === value);
  return found?.label || value || 'All';
}

/**
 * Dedicated Against PO IN/OUT Record — purchase orders (OUT) and Against PO receiving (IN).
 */
const AgainstPoInOutReportPanel = () => {
  const { showToast } = useToast();
  const [subTab, setSubTab] = useState(0);
  const [direction, setDirection] = useState('ALL');
  const [dateFrom, setDateFrom] = useState(() => addDays(todayDateValue(), -90));
  const [dateTo, setDateTo] = useState(() => todayDateValue());
  const [status, setStatus] = useState('');
  const [workflowStatus, setWorkflowStatus] = useState('');
  const [search, setSearch] = useState('');
  const [reportDate, setReportDate] = useState(todayDateValue);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [visualizing, setVisualizing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);
  const [visualizeOpen, setVisualizeOpen] = useState(false);
  const [visualizeData, setVisualizeData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const rows = await fetchAgainstPoInOutReportHistory();
      setHistory(rows);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (subTab === 1) {
      loadHistory();
    }
  }, [subTab, loadHistory]);

  const dateRangeError = useMemo(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return 'Date From must be on or before Date To';
    }
    return '';
  }, [dateFrom, dateTo]);

  const reportPayload = useMemo(() => ({
    direction,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    status,
    workflowStatus,
    search,
    reportDate
  }), [direction, dateFrom, dateTo, status, workflowStatus, search, reportDate]);

  const handlePreview = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setPreviewing(true);
    try {
      const reportData = await fetchAgainstPoInOutReportPreviewData(reportPayload);
      setPreviewReportData(reportData);
      setPreviewOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Preview failed', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleVisualize = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setVisualizing(true);
    try {
      const reportData = await fetchAgainstPoInOutReportPreviewData(reportPayload);
      setVisualizeData(reportData);
      setVisualizeOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Visualization failed', 'error');
    } finally {
      setVisualizing(false);
    }
  };

  const handleDownload = async (visualizations) => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloading(true);
    try {
      const stamp = dateFrom && dateTo
        ? `${dateFrom}_to_${dateTo}`
        : reportDate;
      await downloadAgainstPoInOutReportPdf(
        { ...reportPayload, visualizations },
        `Against_PO_IN_OUT_Record_${stamp}.pdf`
      );
      showToast(
        visualizations?.length
          ? 'Against PO IN/OUT Record with visualizations downloaded and saved to history'
          : 'Against PO IN/OUT Record downloaded and saved to history',
        'success'
      );
      if (subTab === 1) {
        loadHistory();
      }
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Download failed', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloadingCsv(true);
    try {
      const stamp = dateFrom && dateTo
        ? `${dateFrom}_to_${dateTo}`
        : reportDate;
      await downloadAgainstPoInOutReportCsv(
        reportPayload,
        `Against_PO_IN_OUT_Record_${stamp}.pdf`
      );
      showToast('Against PO IN/OUT Record CSV downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'CSV download failed', 'error');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleHistoryDownload = (row) => {
    try {
      downloadAgainstPoInOutReportHistoryPdf(row._id, row.filename || 'Against_PO_IN_OUT_Record.pdf');
      showToast('Re-downloading stored PDF', 'success');
    } catch (err) {
      showToast(err.message || 'Re-download failed', 'error');
    }
  };

  const handleHistoryDownloadCsv = (row) => {
    try {
      downloadAgainstPoInOutReportHistoryCsv(row._id, row.filename || 'Against_PO_IN_OUT_Record.pdf');
      showToast('Downloading CSV', 'success');
    } catch (err) {
      showToast(err.message || 'CSV download failed', 'error');
    }
  };

  const busy = previewing || downloading || downloadingCsv || visualizing;
  const showOutFilters = direction === 'ALL' || direction === 'PO_OUT';
  const showInFilters = direction === 'ALL' || direction === 'PO_IN';

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Against PO IN/OUT Record
      </Typography>
      <Typography color="text.secondary" paragraph>
        Purchase orders (OUT) and Against PO receiving history (IN). Choose direction to include
        one or both. Each PDF download is stored for later re-download.
      </Typography>

      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Generate" />
        <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="History" />
      </Tabs>

      {subTab === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2.5} maxWidth={640}>
            <FormControl fullWidth>
              <InputLabel id="against-po-direction-label">Direction</InputLabel>
              <Select
                labelId="against-po-direction-label"
                value={direction}
                label="Direction"
                onChange={(e) => setDirection(e.target.value)}
              >
                {DIRECTION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Date from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Date to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            {dateRangeError && (
              <Alert severity="warning">{dateRangeError}</Alert>
            )}

            {showOutFilters && (
              <FormControl fullWidth>
                <InputLabel id="against-po-status-label">PO OUT Status</InputLabel>
                <Select
                  labelId="against-po-status-label"
                  value={status}
                  label="PO OUT Status"
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {PO_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {showInFilters && (
              <FormControl fullWidth>
                <InputLabel id="against-po-workflow-label">PO IN Receiving Status</InputLabel>
                <Select
                  labelId="against-po-workflow-label"
                  value={workflowStatus}
                  label="PO IN Receiving Status"
                  onChange={(e) => setWorkflowStatus(e.target.value)}
                >
                  {WORKFLOW_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              placeholder="PO number, supplier…"
            />

            <TextField
              label="Report Date"
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={visualizing ? <CircularProgress size={16} /> : <BarChartIcon />}
                onClick={handleVisualize}
                disabled={busy || Boolean(dateRangeError)}
              >
                Visualize
              </Button>
              <Button
                variant="outlined"
                startIcon={previewing ? <CircularProgress size={16} /> : <Preview />}
                onClick={handlePreview}
                disabled={busy || Boolean(dateRangeError)}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
                onClick={() => handleDownload()}
                disabled={busy || Boolean(dateRangeError)}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={downloadingCsv ? <CircularProgress size={16} /> : <Download />}
                onClick={handleDownloadCsv}
                disabled={busy || Boolean(dateRangeError)}
              >
                Download CSV
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {subTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">Downloaded Against PO IN/OUT Records</Typography>
            <Button
              size="small"
              startIcon={historyLoading ? <CircularProgress size={14} /> : <Refresh />}
              onClick={loadHistory}
              disabled={historyLoading}
            >
              Refresh
            </Button>
          </Box>

          {historyLoading && history.length === 0 ? (
            <Box py={4} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : history.length === 0 ? (
            <Alert severity="info">
              No Against PO IN/OUT Record downloads yet. Generate one from the Generate tab.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Downloaded</TableCell>
                  <TableCell>Generated by</TableCell>
                  <TableCell>Direction</TableCell>
                  <TableCell>Date range</TableCell>
                  <TableCell align="right">Rows</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDisplayDateTime(row.downloadedAt)}</TableCell>
                    <TableCell>{formatHistoryUserName(row.downloadedBy)}</TableCell>
                    <TableCell>{directionLabel(row.direction)}</TableCell>
                    <TableCell>
                      {row.dateFrom || row.dateTo
                        ? `${formatDisplayDate(row.dateFrom)} – ${formatDisplayDate(row.dateTo)}`
                        : 'All dates'}
                    </TableCell>
                    <TableCell align="right">{row.rowCount ?? '—'}</TableCell>
                    <TableCell align="right">{formatFileSize(row.fileSize)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownload(row)}
                        >
                          PDF
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownloadCsv(row)}
                        >
                          CSV
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <ReportPreviewDialog
        open={previewOpen}
        title="Against PO IN/OUT Record preview"
        reportData={previewReportData}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewReportData(null);
        }}
        onDownload={() => handleDownload()}
        downloading={downloading}
        onDownloadCsv={handleDownloadCsv}
        downloadingCsv={downloadingCsv}
      />

      <ReportVisualizeDialog
        open={visualizeOpen}
        title="Against PO IN/OUT Record"
        data={visualizeData}
        downloading={downloading}
        onClose={() => {
          setVisualizeOpen(false);
          setVisualizeData(null);
        }}
        onDownloadPdfWithVisualization={(visualizations) => handleDownload(visualizations)}
      />
    </Box>
  );
};

export default AgainstPoInOutReportPanel;
